import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Check, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { calculatePhaseProgress } from "@/lib/dashboardHelpers";
import { useNavigate } from "react-router-dom";

interface JourneyProgressWidgetProps {
  userId: string;
  unaId?: string;
  experienceLevel: 'new' | 'learning' | 'power';
}

interface JourneyPhase {
  phase_number: number;
  phase_name: string;
  phase_description: string;
  order_number: number;
}

export const JourneyProgressWidget = ({
  userId,
  unaId,
  experienceLevel,
}: JourneyProgressWidgetProps) => {
  const navigate = useNavigate();

  const { data: journeyState } = useQuery({
    queryKey: ['journey-state', userId],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_journey_state')
        .select('current_phase, collapsed_phases')
        .eq('user_id', userId)
        .single();
      return data;
    },
  });

  const { data: phases } = useQuery({
    queryKey: ['journey-phases'],
    queryFn: async () => {
      const { data } = await supabase
        .from('journey_phases')
        .select('*')
        .order('order_number');
      return (data || []) as JourneyPhase[];
    },
  });

  const { data: phaseProgress } = useQuery({
    queryKey: ['phase-progress', unaId],
    queryFn: async () => {
      if (!unaId) return {};
      
      const progress: Record<number, number> = {};
      
      // Calculate progress for each phase
      for (const phase of phases || []) {
        progress[phase.phase_number] = await calculatePhaseProgress(
          unaId,
          phase.phase_number
        );
      }
      
      return progress;
    },
    enabled: !!unaId && !!phases,
  });

  const currentPhase = journeyState?.current_phase || 1;
  const collapsedPhases = journeyState?.collapsed_phases || [];

  // Filter phases based on experience level
  const visiblePhases = phases?.filter(phase => {
    if (experienceLevel === 'new') {
      return phase.phase_number === 1;
    }
    return true;
  }) || [];

  const getPhaseStatus = (phaseNum: number): 'completed' | 'current' | 'upcoming' => {
    if (collapsedPhases.includes(phaseNum)) return 'completed';
    if (phaseNum < currentPhase) return 'completed';
    if (phaseNum === currentPhase) return 'current';
    return 'upcoming';
  };

  const handleContinue = (phaseNum: number) => {
    navigate('/workflows');
  };

  if (!phases || phases.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">🎯 Your Journey to Operations</CardTitle>
        <CardDescription>
          Complete workflows in each phase to build your UNA foundation
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {visiblePhases.map((phase) => {
          const status = getPhaseStatus(phase.phase_number);
          const progress = phaseProgress?.[phase.phase_number] || 0;

          return (
            <div
              key={phase.phase_number}
              className={`p-4 border rounded-lg transition-all ${
                status === 'current'
                  ? 'border-primary bg-primary/5'
                  : status === 'completed'
                  ? 'border-green-500/30 bg-green-500/5'
                  : 'border-border bg-muted/20'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {status === 'completed' ? (
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    ) : status === 'upcoming' ? (
                      <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                        <Lock className="w-4 h-4 text-muted-foreground" />
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-bold">
                        {phase.phase_number}
                      </div>
                    )}
                    <h3 className="font-semibold text-lg">{phase.phase_name}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground ml-8">
                    {phase.phase_description}
                  </p>
                </div>
                {status === 'current' && (
                  <Button size="sm" onClick={() => handleContinue(phase.phase_number)}>
                    Continue
                  </Button>
                )}
                {status === 'upcoming' && experienceLevel !== 'new' && (
                  <Button size="sm" variant="outline" disabled>
                    Locked
                  </Button>
                )}
              </div>

              {status !== 'upcoming' && (
                <div className="ml-8">
                  <div className="flex items-center gap-2">
                    <Progress value={progress} className="flex-1" />
                    <span className="text-sm text-muted-foreground min-w-[3rem]">
                      {progress}%
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
